/**
 * Schedules a callback to run when the browser is idle, so that third-party
 * scripts (marketing/analytics tags) do not compete with hydration on the
 * main thread.
 *
 * Safari does not support `requestIdleCallback`, so we fall back to
 * `setTimeout` with a short delay — enough to yield past the initial render.
 *
 * Returns a cleanup function that cancels the pending callback if it has
 * not run yet.
 */
export function scheduleIdle(
	callback: () => void,
	options?: { timeout?: number },
): () => void {
	if (typeof window === "undefined") {
		return () => {};
	}

	if (typeof window.requestIdleCallback === "function") {
		const handle = window.requestIdleCallback(() => callback(), options);
		return () => window.cancelIdleCallback?.(handle);
	}

	const delay = options?.timeout ?? 1;
	const handle = window.setTimeout(callback, delay);
	return () => window.clearTimeout(handle);
}
